/**
 * EPI Form Component
 * Form for creating and editing EPI data
 *
 * @version 2025.2.0
 */
import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Paper,
  Typography,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { Epi, EpiStatus, TipoDeRisco, EpiFormData } from '../../../types/epi';
import { centsToReais, reaisToCents } from '../../../utils/financialHelpers';

// Validation schema
const validationSchema = yup.object({
  nome: yup
    .string()
    .required('Nome do EPI é obrigatório')
    .min(3, 'Mínimo de 3 caracteres')
    .max(255, 'Máximo de 255 caracteres'),
  tipoDeRisco: yup
    .string()
    .oneOf(Object.values(TipoDeRisco), 'Tipo de risco inválido')
    .required('Tipo de risco é obrigatório'),
  risco: yup
    .string()
    .required('Descrição do risco é obrigatória')
    .max(255, 'Máximo de 255 caracteres'),
  eSocial: yup.string().max(50, 'Máximo de 50 caracteres'),
  ativo: yup.string().oneOf(Object.values(EpiStatus)).required(),
  inicioValidade: yup.string().nullable(),
  fimValidade: yup
    .string()
    .required('Data de validade do CA é obrigatória')
    .test('not-past', 'Não é permitido cadastrar EPI com CA vencido', function(value) {
      if (!value) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const expirationDate = new Date(value);
      return expirationDate >= today;
    }),
  atenuacao: yup.string().max(100, 'Máximo de 100 caracteres'),
  metodo: yup
    .string()
    .required('Número do C.A. é obrigatório')
    .max(255, 'Máximo de 255 caracteres'),
  custo: yup
    .number()
    .nullable()
    .transform((value, original) => (original === '' ? null : value))
    .min(0, 'Custo não pode ser negativo'),
  quantidade: yup
    .number()
    .nullable()
    .transform((value, original) => (original === '' ? null : value))
    .min(0, 'Quantidade não pode ser negativa')
    .integer('Quantidade deve ser um número inteiro'),
  unidade: yup.string().max(50, 'Máximo de 50 caracteres'),
  observacao: yup.string().max(2000, 'Máximo de 2000 caracteres'),
  orientacaoDeUso: yup.string().max(5000, 'Máximo de 5000 caracteres'),
});

interface EPIFormProps {
  initialData?: Partial<Epi>;
  onSubmit: (data: EpiFormData) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export const EPIForm: React.FC<EPIFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const isEditing = Boolean(initialData?.id);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<EpiFormData>({
    resolver: yupResolver(validationSchema as yup.ObjectSchema<EpiFormData>),
    defaultValues: {
      nome: initialData?.nome || '',
      tipoDeRisco: initialData?.tipoDeRisco || TipoDeRisco.FISICO,
      risco: initialData?.risco || '',
      eSocial: initialData?.eSocial || '',
      ativo: initialData?.ativo || EpiStatus.ATIVO,
      inicioValidade: initialData?.inicioValidade || '',
      fimValidade: initialData?.fimValidade || '',
      atenuacao: initialData?.atenuacao || '',
      metodo: initialData?.metodo || '',
      custo: centsToReais(initialData?.custo),
      quantidade: initialData?.quantidade || undefined,
      unidade: initialData?.unidade || '',
      observacao: initialData?.observacao || '',
      orientacaoDeUso: initialData?.orientacaoDeUso || '',
    },
  });

  const handleFormSubmit = async (data: EpiFormData) => {
    // Convert custo from reais to centavos if provided
    const submissionData = {
      ...data,
      custo: reaisToCents(data.custo),
    };
    await onSubmit(submissionData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit(handleFormSubmit)} noValidate>
      {/* Identification Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Identificação
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          {/* Nome */}
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name="nome"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Nome do EPI"
                  fullWidth
                  required
                  error={Boolean(errors.nome)}
                  helperText={errors.nome?.message || 'Ex: Protetor Auricular tipo Plug'}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>

          {/* Tipo de Risco */}
          <Grid
            size={{
              xs: 12,
              md: 3
            }}>
            <FormControl fullWidth required error={Boolean(errors.tipoDeRisco)}>
              <InputLabel>Tipo de Risco</InputLabel>
              <Controller
                name="tipoDeRisco"
                control={control}
                render={({ field }) => (
                  <Select {...field} label="Tipo de Risco" disabled={isSubmitting}>
                    <MenuItem value={TipoDeRisco.FISICO}>Físico</MenuItem>
                    <MenuItem value={TipoDeRisco.QUIMICO}>Químico</MenuItem>
                    <MenuItem value={TipoDeRisco.BIOLOGICO}>Biológico</MenuItem>
                    <MenuItem value={TipoDeRisco.ERGONOMICO}>Ergonômico</MenuItem>
                    <MenuItem value={TipoDeRisco.ACIDENTE}>Acidente</MenuItem>
                  </Select>
                )}
              />
              {errors.tipoDeRisco && <FormHelperText>{errors.tipoDeRisco.message}</FormHelperText>}
            </FormControl>
          </Grid>

          {/* Status */}
          <Grid
            size={{
              xs: 12,
              md: 3
            }}>
            <FormControl fullWidth required>
              <InputLabel>Status</InputLabel>
              <Controller
                name="ativo"
                control={control}
                render={({ field }) => (
                  <Select {...field} label="Status" disabled={isSubmitting}>
                    <MenuItem value={EpiStatus.ATIVO}>Ativo</MenuItem>
                    <MenuItem value={EpiStatus.INATIVO}>Inativo</MenuItem>
                  </Select>
                )}
              />
            </FormControl>
          </Grid>

          {/* Risco */}
          <Grid size={12}>
            <Controller
              name="risco"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Descrição do Risco"
                  fullWidth
                  required
                  error={Boolean(errors.risco)}
                  helperText={errors.risco?.message || 'Ex: Ruído contínuo acima de 85 dB(A)'}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* CA Information Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Certificado de Aprovação (C.A.)
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          {/* C.A. Number */}
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Controller
              name="metodo"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Número do C.A."
                  fullWidth
                  required
                  error={Boolean(errors.metodo)}
                  helperText={errors.metodo?.message || 'Ex: 12345'}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>

          {/* Início Validade */}
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Controller
              name="inicioValidade"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Início da Validade"
                  fullWidth
                  type="date"
                  slotProps={{ inputLabel: { shrink: true } }}
                  error={Boolean(errors.inicioValidade)}
                  helperText={errors.inicioValidade?.message}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>

          {/* Fim Validade */}
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Controller
              name="fimValidade"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Validade do C.A."
                  fullWidth
                  required
                  type="date"
                  slotProps={{ inputLabel: { shrink: true } }}
                  error={Boolean(errors.fimValidade)}
                  helperText={errors.fimValidade?.message || 'Data de vencimento do C.A.'}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>

          {/* Atenuação */}
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name="atenuacao"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Fator de Atenuação"
                  fullWidth
                  error={Boolean(errors.atenuacao)}
                  helperText={errors.atenuacao?.message || 'Ex: NRRsf 18 dB'}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>

          {/* eSocial Code */}
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name="eSocial"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Código eSocial"
                  fullWidth
                  error={Boolean(errors.eSocial)}
                  helperText={errors.eSocial?.message || 'Código da tabela eSocial'}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Stock Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Estoque e Custos
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          {/* Quantidade */}
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Controller
              name="quantidade"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  label="Quantidade em Estoque"
                  fullWidth
                  type="number"
                  error={Boolean(errors.quantidade)}
                  helperText={errors.quantidade?.message}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>

          {/* Unidade */}
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Controller
              name="unidade"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Unidade de Medida"
                  fullWidth
                  error={Boolean(errors.unidade)}
                  helperText={errors.unidade?.message || 'Ex: par, unidade, kit'}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>

          {/* Custo */}
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Controller
              name="custo"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ? field.value / 100 : ''}
                  onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) * 100 : null)}
                  label="Custo Unitário"
                  fullWidth
                  type="number"
                  slotProps={{
                    input: {
                      startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                    },
                  }}
                  error={Boolean(errors.custo)}
                  helperText={errors.custo?.message}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Observations Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Observações
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          <Grid size={12}>
            <Controller
              name="orientacaoDeUso"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Orientação de uso"
                  fullWidth
                  multiline
                  rows={4}
                  error={Boolean(errors.orientacaoDeUso)}
                  helperText={
                    errors.orientacaoDeUso?.message ||
                    'Como usar, higienizar e guardar o EPI. Aparece no anexo de EPIs do PGR.'
                  }
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>
          <Grid size={12}>
            <Controller
              name="observacao"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Observações"
                  fullWidth
                  multiline
                  rows={4}
                  error={Boolean(errors.observacao)}
                  helperText={errors.observacao?.message}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          type="button"
          onClick={onCancel}
          variant="outlined"
          startIcon={<CancelIcon />}
          disabled={isSubmitting}
        >
          Cancelar
        </Button>

        <Button
          type="submit"
          variant="contained"
          startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={isSubmitting}
        >
          {isEditing ? 'Atualizar' : 'Criar'} EPI
        </Button>
      </Box>
    </Box>
  );
};

export default EPIForm;
